// partners_view/src/apis/adminApiSlice.js
// RTK Query slice for all admin API endpoints
// Covers all 17 Task-8 backend routes (+ a few helpers)

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { API_ORIGIN } from "../Utils";

const BASE_URL = API_ORIGIN;

export const adminApiSlice = createApi({
  reducerPath: "adminApi",
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers) => {
      const token =
        localStorage.getItem("accessToken") ||
        (() => {
          try {
            return JSON.parse(localStorage.getItem("partnersViewUser") || "{}")
              ?.accessToken;
          } catch {
            return null;
          }
        })();
      if (token) headers.set("authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: [
    "AdminStats",
    "Applications",
    "Partners",
    "Commissions",
    "Payouts",
    "Milestones",
    "Interest",
  ],
  endpoints: (builder) => ({

    // ── Stats ──────────────────────────────────────────────
    getAdminStats: builder.query({
      query: () => "partners/api/admin/partners/stats/",
      providesTags: ["AdminStats"],
    }),

    // ── Applications ───────────────────────────────────────
    getAdminApplications: builder.query({
      query: ({ status = "", type = "" } = {}) => ({
        url: "partners/api/admin/partners/applications/",
        params: { status, type },
      }),
      providesTags: ["Applications"],
    }),

    getAdminApplicationDetail: builder.query({
      query: (id) => `partners/api/admin/partners/applications/${id}/`,
      providesTags: (result, error, id) => [{ type: "Applications", id }],
    }),

    exportApplicationsCSV: builder.query({
      query: ({ status = "", type = "" } = {}) => ({
        url: "partners/api/admin/partners/applications/export/",
        params: { status, type },
        responseHandler: (response) => response.text(),
      }),
    }),

    reviewApplication: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `partners/api/admin/partners/applications/${id}/review/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Applications", "AdminStats", "Partners"],
    }),

    // ── Partners ───────────────────────────────────────────
    getAdminPartners: builder.query({
      query: ({ search = "", tier = "", type = "", is_active = "" } = {}) => ({
        url: "partners/api/admin/partners/partners/",
        params: {
          search,
          tier,
          type,
          ...(is_active !== "" ? { is_active } : {}),
        },
      }),
      providesTags: ["Partners"],
    }),

    getAdminPartnerDetail: builder.query({
      query: (id) => `partners/api/admin/partners/partners/${id}/`,
      providesTags: (result, error, id) => [{ type: "Partners", id }],
    }),

    updatePartner: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `partners/api/admin/partners/partners/${id}/update/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Partners", id },
        "AdminStats",
      ],
    }),

    registerFundAccount: builder.mutation({
      query: (id) => ({
        url: `partners/api/admin/partners/partners/${id}/register-fund-account/`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [{ type: "Partners", id }],
    }),

    // ── Commissions ────────────────────────────────────────
    getAdminCommissions: builder.query({
      query: ({ month = "", year = "", status = "", partner_type = "" } = {}) => ({
        url: "partners/api/admin/partners/commissions/",
        params: { month, year, status, partner_type },
      }),
      providesTags: ["Commissions"],
    }),

    confirmCommission: builder.mutation({
      query: (id) => ({
        url: `partners/api/admin/partners/commissions/${id}/confirm/`,
        method: "POST",
      }),
      invalidatesTags: ["Commissions"],
    }),

    calculateCommissions: builder.mutation({
      query: ({ month, year }) => ({
        url: "partners/api/admin/partners/commissions/calculate/",
        method: "POST",
        body: { month, year },
      }),
      invalidatesTags: ["Commissions", "AdminStats"],
    }),

    confirmCommissions: builder.mutation({
      query: ({ month, year }) => ({
        url: "partners/api/admin/partners/commissions/confirm/",
        method: "POST",
        body: { month, year },
      }),
      invalidatesTags: ["Commissions", "AdminStats", "Payouts"],
    }),

    preparePayouts: builder.mutation({
      query: ({ month, year }) => ({
        url: "partners/api/admin/partners/payouts/prepare/",
        method: "POST",
        body: { month, year },
      }),
      invalidatesTags: ["Payouts", "AdminStats"],
    }),

    // ── Payouts ────────────────────────────────────────────
    getAdminPayouts: builder.query({
      query: ({ month = "", year = "", status = "" } = {}) => ({
        url: "partners/api/admin/partners/payouts/",
        params: { month, year, status },
      }),
      providesTags: ["Payouts"],
    }),

    getAdminPayoutDetail: builder.query({
      query: (id) => `partners/api/admin/partners/payouts/${id}/`,
      providesTags: (result, error, id) => [{ type: "Payouts", id }],
    }),

    retryPayout: builder.mutation({
      query: (id) => ({
        url: `partners/api/admin/partners/payouts/${id}/retry/`,
        method: "POST",
      }),
      invalidatesTags: ["Payouts"],
    }),

    triggerPayouts: builder.mutation({
      query: ({ month, year }) => ({
        url: "partners/api/admin/partners/payouts/trigger/",
        method: "POST",
        body: { month, year },
      }),
      invalidatesTags: ["Payouts", "AdminStats"],
    }),

    retryFailedPayouts: builder.mutation({
      query: ({ month, year }) => ({
        url: "partners/api/admin/partners/payouts/retry-all/",
        method: "POST",
        body: { month, year },
      }),
      invalidatesTags: ["Payouts"],
    }),

    // ── Milestones ─────────────────────────────────────────
    getAdminMilestones: builder.query({
      query: ({ paid = "false" } = {}) => ({
        url: "partners/api/admin/partners/milestones/",
        params: { paid },
      }),
      providesTags: ["Milestones"],
    }),

    markMilestonePaid: builder.mutation({
      query: (id) => ({
        url: `partners/api/admin/partners/milestones/${id}/mark-paid/`,
        method: "POST",
      }),
      invalidatesTags: ["Milestones", "AdminStats"],
    }),

    // ── Coming Soon Interest ───────────────────────────────
    getAdminInterest: builder.query({
      query: ({ track = "" } = {}) => ({
        url: "partners/api/admin/partners/interest/",
        params: { track },
      }),
      providesTags: ["Interest"],
    }),

    exportInterestCSV: builder.query({
      query: ({ track = "" } = {}) => ({
        url: "partners/api/admin/partners/interest/export/",
        params: { track },
        responseHandler: (response) => response.text(),
      }),
    }),
  }),
});

export const {
  useGetAdminStatsQuery,
  useGetAdminApplicationsQuery,
  useGetAdminApplicationDetailQuery,
  useExportApplicationsCSVQuery,
  useReviewApplicationMutation,
  useGetAdminPartnersQuery,
  useGetAdminPartnerDetailQuery,
  useUpdatePartnerMutation,
  useRegisterFundAccountMutation,
  useGetAdminCommissionsQuery,
  useConfirmCommissionMutation,
  useCalculateCommissionsMutation,
  useConfirmCommissionsMutation,
  usePreparePayoutsMutation,
  useGetAdminPayoutsQuery,
  useGetAdminPayoutDetailQuery,
  useRetryPayoutMutation,
  useTriggerPayoutsMutation,
  useRetryFailedPayoutsMutation,
  useGetAdminMilestonesQuery,
  useMarkMilestonePaidMutation,
  useGetAdminInterestQuery,
  useExportInterestCSVQuery,
} = adminApiSlice;
